import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { httpsCallable } from '../lib/callable';
import { functions } from '../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../constants/firebase';
import { useAuth } from '../contexts/AuthContext';
import { BrandLogo, useBranding } from '../contexts/BrandingContext';
import { shouldShowAdminNav } from '../features/admin/adminAccess';
import { createProjectErrorMessage } from '../features/projects/projectLimitMessage';
import { useToast } from '../contexts/ToastContext';
import ProjectCreationWizard from '../components/ProjectCreationWizard';
import ProtocolUploader from '../components/ProtocolUploader';
import UploadCrfPacketModal from '../components/UploadCrfPacketModal';
import {
  Plus,
  FolderKanban,
  Clock,
  LogOut,
  CheckCircle2,
  X,
  Shield,
  ShieldAlert,
  Archive
} from 'lucide-react';
import { dashboardEmptyState } from './dashboardEmptyState';
import { shouldRecheckOrg } from '../features/orgs/orgAccess';
import { sortProjectsNewestFirst, protocolFileDisplayName } from '../features/projects/dashboardCards';

interface Project {
  id: string;
  name: string;
  shortName: string;
  description: string;
  phase: string;
  studyType: string;
  /** Creation time in ms; absent when the project carries none. */
  createdAt?: number;
  /** Stored path of the uploaded protocol, when the list supplies it. */
  protocolFilePath?: string;
}

export default function Dashboard() {
  const { branding } = useBranding();
  // `loading` is aliased: this component already has its own `loading` for the
  // project list, which is unrelated to whether the admin check has resolved.
  const { user, logout, loading: authLoading, isAdmin } = useAuth();
  const showAdminNav = shouldShowAdminNav({
    loading: authLoading,
    signedIn: !!user,
    isAdmin,
  });
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  /** What an empty list MEANS — nothing owned, or nothing visible. */
  const [emptyState, setEmptyState] = useState(dashboardEmptyState({ projects: [] }));
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showWizard, setShowWizard] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [showCrfPacketUploader, setShowCrfPacketUploader] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false); // Manual form
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedProjectName, setSelectedProjectName] = useState<string>('');
  const [selectedShortName, setSelectedShortName] = useState<string>('');
  const [selectedTemplateStandard, setSelectedTemplateStandard] = useState<string>('general');

  const [creating, setCreating] = useState(false);
  const { showToast } = useToast();

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);

  // New project form state
  const [newProject, setNewProject] = useState({
    name: '',
    shortName: '',
    description: '',
    studyType: 'interventional',
    isRandomized: false,
    requiresPart11: true,
    sponsorName: '',
  });

  useEffect(() => {
    loadProjects();
  }, [user]);

  const loadProjects = async (alreadyRechecked = false) => {
    if (!user) return;

    // Server-side lookup. Sidesteps the rules-engine restriction on
    // collection-group queries that contain cross-document predicates,
    // and unions membership / createdBy / piUserId in one round trip.
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.LIST_MY_PROJECTS, { timeout: CALLABLE_TIMEOUTS.SHORT });
      const res = await fn({});
      const data = res.data as {
        projects: Array<{
          id: string;
          name: string;
          shortName?: string;
          description?: string;
          phase?: string;
          studyType?: string;
          createdAt?: number;
          protocolFilePath?: string;
        }>;
        /** Set when this account carries no organisation — see below. */
        orgMissing?: boolean;
      };
      // A sign-in taken before the account gained its organisation still
      // carries none. Refresh it once and look again before saying so: on
      // 2026-09-29 that stale sign-in was the whole of a customer's problem.
      if (
        data.projects.length === 0 &&
        shouldRecheckOrg({ orgMissing: data.orgMissing === true, alreadyRechecked })
      ) {
        await user.getIdToken(true);
        return loadProjects(true);
      }
      setEmptyState(dashboardEmptyState(data));
      const projectList: Project[] = data.projects.map(p => ({
        id: p.id,
        name: p.name ?? '',
        shortName: p.shortName ?? '',
        description: p.description ?? '',
        phase: p.phase || 'planning',
        studyType: p.studyType ?? '',
        createdAt: p.createdAt || undefined,
        protocolFilePath: p.protocolFilePath,
      }));
      setProjects(sortProjectsNewestFirst(projectList));
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Check for duplicate name
    const duplicateName = projects.find(
      p => p.name.toLowerCase() === newProject.name.toLowerCase()
    );
    if (duplicateName) {
      showToast('A project with this name already exists. Please choose a different name.', 'error');
      return;
    }

    // Check for duplicate short name
    const duplicateShortName = projects.find(
      p => p.shortName.toLowerCase() === newProject.shortName.toLowerCase()
    );
    if (duplicateShortName) {
      showToast('A project with this short name already exists. Please choose a different short name.', 'error');
      return;
    }

    setCreating(true);

    try {
      // Use Cloud Function to create project
      const createProjectFn = httpsCallable(functions, CLOUD_FUNCTIONS.CREATE_PROJECT, { timeout: CALLABLE_TIMEOUTS.SHORT });
      await createProjectFn(newProject);

      // Reset form and reload
      setNewProject({
        name: '',
        shortName: '',
        description: '',
        studyType: 'interventional',
        isRandomized: false,
        requiresPart11: true,
        sponsorName: '',
      });
      setShowNewProject(false);
      loadProjects();
    } catch (error: unknown) {
      console.error('Error creating project:', error);
      // Never render the callable's own message: it leaks server wording, and
      // on an unmapped failure it says nothing a user can act on. The helper
      // keys on the structured `details.reason`, so a project-limit refusal
      // reads as a limit rather than as a mystery failure.
      showToast(createProjectErrorMessage(error), 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (project: Project) => {
    if (!user) return;

    setDeleting(true);

    try {
      // Archive (soft-delete): the archiveProject callable flags the project
      // and hides it from the dashboard. It stays recoverable from the admin
      // archive page until the 30-day auto-purge. This replaces the old
      // client-side deleteDoc, which stranded every subcollection.
      const archiveFn = httpsCallable(functions, CLOUD_FUNCTIONS.ARCHIVE_PROJECT, { timeout: CALLABLE_TIMEOUTS.SHORT });
      await archiveFn({ projectId: project.id });

      // Remove from local state
      setProjects(projects.filter(p => p.id !== project.id));
      setDeleteConfirm(null);
      showToast(`"${project.name}" moved to archive. Recoverable for 30 days.`, 'success');
    } catch (error: any) {
      console.error('Error archiving project:', error);
      showToast(error?.message || 'Failed to archive project. Please try again.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const getPhaseColor = (phase: string) => {
    const colors: Record<string, string> = {
      planning: 'bg-[#EEF1F2] text-[#4D5C60]',
      protocol_development: 'bg-[#E6F4F2] text-[var(--brand-primary)]',
      data_collection_design: 'bg-[#E6F4F2] text-[var(--brand-primary)]',
      system_build: 'bg-[#FFF4DF] text-[#9A5A06]',
      validation_testing: 'bg-[#FFF4DF] text-[#9A5A06]',
      active: 'bg-[#E9F6EF] text-[#16784B]',
      analysis: 'bg-[#E6F4F2] text-[var(--brand-primary)]',
      closeout: 'bg-[#EEF1F2] text-[#4D5C60]',
    };
    return colors[phase] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#F7F9FB] px-5 py-6 text-[#0B1C30] sm:px-8 lg:px-12 lg:py-10"
      style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        backgroundImage:
          'radial-gradient(circle at 94% 2%, rgba(0,137,137,.11), transparent 25%), radial-gradient(circle at 5% 98%, rgba(255,185,92,.08), transparent 24%)',
      }}
    >
      {/* Header */}
      <div className="mx-auto mb-9 flex max-w-[1440px] flex-col gap-6 border-b border-[#DCE2E5] pb-7 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="mb-4 flex items-center gap-3">
            <BrandLogo className="h-10 w-10 rounded-[10px] object-contain" />
            <div>
              <p className="text-base font-bold leading-tight text-[#001820]">{branding.productName}</p>
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#5F696C]">Study Build Automation</p>
            </div>
          </div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-[var(--brand-primary)]">Study workspace</p>
          <h1 className="text-4xl font-normal tracking-[-0.04em] text-[#001820]">Your projects</h1>
          <p className="mt-2 text-[15px] text-[#5F696C]">Manage your clinical research projects and study builds.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <span className="hidden text-sm text-[#5F696C] sm:inline">{user?.email}</span>
          {/* Single administration entry point, shown only to a resolved
              administrator. Previously two ungated links (Instruction Library,
              Archive) sat here and were visible to every signed-in customer.
              A non-administrator sees nothing at all — no disabled control and
              no message, so the area's existence is not disclosed. */}
          {showAdminNav && (
            <Link
              to="/admin"
              className="flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-[#35565C] transition hover:bg-white hover:shadow-sm"
            >
              <Shield className="w-4 h-4" />
              Administration
            </Link>
          )}
          <button onClick={logout} title="Sign out" className="flex items-center gap-2 rounded-full p-2.5 text-[#5F696C] transition hover:bg-white hover:text-[#001820] hover:shadow-sm">
            <LogOut className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowWizard(true)}
            className="flex items-center space-x-2 rounded-full bg-[#001820] px-5 py-3 text-sm font-bold text-white shadow-[0_8px_20px_rgba(0,24,32,0.16)] transition hover:bg-[#00313A]"
          >
            <Plus className="h-5 w-5" />
            <span>New Project</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mx-auto mb-10 grid max-w-[1440px] grid-cols-1 gap-5 md:grid-cols-3">
        <div className="rounded-2xl border border-[#DCE2E5] bg-white p-6 shadow-[0_10px_30px_rgba(15,42,50,0.06)]">
          <div className="flex items-center space-x-3">
            <div className="rounded-xl bg-[#E6F4F2] p-3">
              <FolderKanban className="h-6 w-6 text-[var(--brand-primary)]" />
            </div>
            <div>
              <p className="text-3xl font-normal text-[#001820]">{projects.length}</p>
              <p className="text-sm font-medium text-[#5F696C]">Total projects</p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-[#DCE2E5] bg-white p-6 shadow-[0_10px_30px_rgba(15,42,50,0.06)]">
          <div className="flex items-center space-x-3">
            <div className="rounded-xl bg-[#E9F6EF] p-3">
              <CheckCircle2 className="h-6 w-6 text-[#16784B]" />
            </div>
            <div>
              <p className="text-3xl font-normal text-[#001820]">
                {projects.filter(p => p.phase === 'active').length}
              </p>
              <p className="text-sm font-medium text-[#5F696C]">Active studies</p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-[#DCE2E5] bg-white p-6 shadow-[0_10px_30px_rgba(15,42,50,0.06)]">
          <div className="flex items-center space-x-3">
            <div className="rounded-xl bg-[#FFF4DF] p-3">
              <Clock className="h-6 w-6 text-[#B46B08]" />
            </div>
            <div>
              <p className="text-3xl font-normal text-[#001820]">
                {projects.filter(p => ['planning', 'protocol_development', 'system_build'].includes(p.phase)).length}
              </p>
              <p className="text-sm font-medium text-[#5F696C]">In setup</p>
            </div>
          </div>
        </div>
      </div>

      {/* Project List */}
      {projects.length === 0 ? (
        <div className="mx-auto max-w-[1440px] rounded-[22px] border border-[#DCE2E5] bg-white p-12 text-center shadow-[0_18px_48px_rgba(15,42,50,0.08)]">
          {/* An empty list has two meanings and they must not look alike — see
              dashboardEmptyState.ts for what happened when they did. */}
          {emptyState.kind === 'no-organisation' ? (
            <ShieldAlert className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          ) : (
            <FolderKanban className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          )}
          <h3 className="text-lg font-medium text-gray-900 mb-2">{emptyState.title}</h3>
          <p className="mx-auto mb-4 max-w-xl text-gray-600">{emptyState.body}</p>
          {emptyState.kind === 'no-organisation' && (
            <button
              onClick={() => {
                setLoading(true);
                loadProjects();
              }}
              className="inline-flex items-center space-x-2 rounded-full bg-[#001820] px-5 py-3 font-bold text-white transition hover:bg-[#00313A]"
            >
              <span>Check again</span>
            </button>
          )}
          {emptyState.showCreateCta && (
            <button
              onClick={() => setShowWizard(true)}
              className="inline-flex items-center space-x-2 rounded-full bg-[#001820] px-5 py-3 font-bold text-white transition hover:bg-[#00313A]"
            >
              <Plus className="h-5 w-5" />
              <span>Create Project</span>
            </button>
          )}
        </div>
      ) : (
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {projects.map((project) => (
            <div
              key={project.id}
              className="group relative overflow-hidden rounded-[20px] border border-[#DCE2E5] bg-white p-6 shadow-[0_10px_30px_rgba(15,42,50,0.06)] transition-all hover:-translate-y-0.5 hover:border-[#73A9A6] hover:shadow-[0_18px_42px_rgba(15,42,50,0.11)]"
            >
              {/* Archive button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDeleteConfirm(project);
                }}
                className="absolute top-3 right-3 p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                title="Archive project"
              >
                <Archive className="h-4 w-4" />
              </button>

              <Link to={`/project/${project.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="rounded-xl bg-[#E6F4F2] p-3">
                    <FolderKanban className="h-6 w-6 text-[var(--brand-primary)]" />
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPhaseColor(project.phase)}`}>
                    {project.phase.replace(/_/g, ' ')}
                  </span>
                </div>
                <h3 className="mb-1 text-lg font-bold text-[#001820]">{project.name}</h3>
                {protocolFileDisplayName(project.protocolFilePath) && (
                  <p className="mb-1 truncate text-xs text-[#5F696C]" title={protocolFileDisplayName(project.protocolFilePath) ?? undefined}>
                    {protocolFileDisplayName(project.protocolFilePath)}
                  </p>
                )}
                <p className="mb-5 min-h-[2.5rem] text-sm leading-5 text-[#5F696C] line-clamp-2">{project.description || 'No description added yet.'}</p>
                <div className="flex items-center justify-between border-t border-[#EDF0F1] pt-4 text-xs text-[#5F696C]">
                  <span className="capitalize">{project.studyType}</span>
                  <span>{project.createdAt ? new Date(project.createdAt).toLocaleDateString() : ''}</span>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Archive className="h-6 w-6 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Archive Project</h2>
            </div>

            <p className="text-gray-600 mb-2">
              Archive <strong>{deleteConfirm.name}</strong>?
            </p>
            <p className="text-sm text-gray-600 mb-6">
              It will be hidden from your dashboard but kept safely. An admin can restore it
              from the Archive, or permanently delete it. If left, it is automatically deleted
              30 days after archiving.
            </p>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteProject(deleteConfirm)}
                disabled={deleting}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Archiving...' : 'Archive Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wizard Modal */}
      {showWizard && (
        <ProjectCreationWizard
          onClose={() => setShowWizard(false)}
          onUploadProtocol={(file, name, shortName, templateStandard) => {
            setSelectedFile(file);
            setSelectedProjectName(name || '');
            setSelectedShortName(shortName || '');
            setSelectedTemplateStandard(templateStandard || 'general');
            setShowWizard(false);
            setShowUploader(true);
          }}
          onUploadCrfPacket={(shortName) => {
            // Carry the wizard's answer into the modal's own field.
            setSelectedShortName(shortName || '');
            setShowWizard(false);
            setShowCrfPacketUploader(true);
          }}
          onCreateManually={(shortName) => {
            // Functional update: the manual form's other fields are whatever
            // the user last left, and only the Short Name is being seeded.
            setNewProject((prev) => ({ ...prev, shortName: shortName || '' }));
            setShowWizard(false);
            setShowNewProject(true);
          }}
        />
      )}

      {/* CRF Packet Upload Modal — creates a new project from a CRF-only upload. */}
      {showCrfPacketUploader && (
        <UploadCrfPacketModal
          defaultSingleForm={false}
          initialShortName={selectedShortName}
          onClose={() => setShowCrfPacketUploader(false)}
          onComplete={(projectId) => {
            setShowCrfPacketUploader(false);
            loadProjects();
            navigate(`/project/${projectId}/crf-builder`);
          }}
        />
      )}

      {/* Protocol Uploader Modal */}
      {showUploader && selectedFile && (
        <ProtocolUploader
          file={selectedFile}
          projectName={selectedProjectName}
          shortName={selectedShortName}
          templateStandard={selectedTemplateStandard}
          onComplete={() => {
            setShowUploader(false);
            setSelectedFile(null);
            loadProjects(); // Refresh list
          }}
          onError={(err, projectId) => {
            console.error(err);
            setShowUploader(false);
            setSelectedFile(null);
            if (projectId) {
              // The study was kept. Open it: its protocol page shows what went
              // wrong and offers the retry.
              loadProjects();
              alert(err);
              navigate(`/project/${projectId}/protocol`);
            } else {
              alert('Upload failed: ' + err);
            }
          }}
        />
      )}

      {/* New Project Modal (Manual) */}
      {showNewProject && (
        <div className="fixed inset-0 bg-gray-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Create New Project</h2>
              <button
                onClick={() => setShowNewProject(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreateProject} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Name *
                </label>
                <input
                  type="text"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g., Phase II Oncology Study"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Short Name *
                </label>
                <input
                  type="text"
                  value={newProject.shortName}
                  onChange={(e) => setNewProject({ ...newProject, shortName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g., ONC-2024-001"
                  maxLength={40}
                  required
                />
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Identifies this study on every document the platform produces, including
                  the filename of each one. Letters, numbers, hyphens and underscores; up to
                  40 characters. You can change it later in Study Settings.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  rows={3}
                  placeholder="Brief description of the study..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Study Type *
                </label>
                <select
                  value={newProject.studyType}
                  onChange={(e) => setNewProject({ ...newProject, studyType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                >
                  <option value="interventional">Interventional</option>
                  <option value="observational">Observational</option>
                  <option value="device">Device</option>
                  <option value="drug">Drug</option>
                  <option value="combination">Combination</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sponsor Name
                </label>
                <input
                  type="text"
                  value={newProject.sponsorName}
                  onChange={(e) => setNewProject({ ...newProject, sponsorName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="Sponsor organization"
                />
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newProject.isRandomized}
                    onChange={(e) => setNewProject({ ...newProject, isRandomized: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Randomized</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newProject.requiresPart11}
                    onChange={(e) => setNewProject({ ...newProject, requiresPart11: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">EDC must meet 21 CFR Part 11</span>
                </label>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNewProject(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
